import React, { Fragment } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SectionHeader } from '@components';
import { BOTTOM_INDENTS, COLORS, defaultTimeRange, disabledPerformanceDate, TOP_INDENTS } from '@constants';
import { useStore } from '@hooks';
import { FILTER_PARAMS } from '@pages/Performance/constants';
import { saveDateRangeToStorage } from '@pages/Performance/utils';
import { Divider, Loader, RangePicker, RedirectLink } from '@ui';
import { dayjs } from '@utils';
import { Row } from 'antd';
import { observer } from 'mobx-react';
import HorizontalCards from '../../Performance/components/Breakdowns/components/HorizontalCards';
import TopProducts from '../../Performance/components/Breakdowns/components/TopProducts';
import TopSites from '../../Performance/components/Breakdowns/components/TopSites';
import { isTouchpointFunnelVisible } from '../../Performance/components/Breakdowns/utils';
import SinglePerformance from './components/SinglePerformance';
import { PERFORMANCE_BLOCKS } from './constants';
import EmptyState from './EmptyState';

const PerformanceSection = observer(({ dateRange }) => {
    const [searchParams] = useSearchParams();
    const store = useStore();
    const {
        pendingMetricsOverview,
        performanceList,
        getMetricsOverview,
        getBreakdowns,
        breakdowns,
        pendingBreakdowns,
        getTopSites,
        topSites,
        pendingTopSites
    } = store.performanceStore;
    const { isBlocked } = store.accountsStore;

    if (!performanceList.length) return <EmptyState />;

    const touchPointFunnelVisible = searchParams.has('touchpointFunnel')
        ? true
        : isTouchpointFunnelVisible(breakdowns?.touchpointFunnel); // use search param for QA testing purpose

    return (
        <div data-e2e='home_performance_section' style={{ position: 'relative' }}>
            <SectionHeader data-e2e='home_performance_title' title='Performance overview'>
                <div data-e2e='home_performance-date-picker_btn'>
                    <RangePicker
                        allowClear={false}
                        key='date-picker'
                        defaultValue={
                            dateRange
                                ? [dayjs(dateRange[FILTER_PARAMS.DATE_FROM]), dayjs(dateRange[FILTER_PARAMS.DATE_TO])]
                                : defaultTimeRange
                        }
                        disabledDate={disabledPerformanceDate}
                        onChange={(data) => {
                            saveDateRangeToStorage({
                                [FILTER_PARAMS.DATE_FROM]: data.min,
                                [FILTER_PARAMS.DATE_TO]: data.max
                            });
                            getMetricsOverview({
                                date_from: data.min,
                                date_to: data.max,
                                metrics: PERFORMANCE_BLOCKS.map((item) => item.label)
                            });
                            getBreakdowns({
                                date_from: data.min,
                                date_to: data.max
                            });
                            getTopSites({
                                date_from: data.min,
                                date_to: data.max
                            });
                        }}
                    />
                </div>
            </SectionHeader>
            <Row
                data-e2e='home_performance-cards_row'
                style={{ ...TOP_INDENTS.L, position: 'relative' }}
                gutter={[16, 16]}
            >
                {pendingMetricsOverview && <Loader overlayColor='rgba(244, 244, 244, 0.8)' />}
                {pendingMetricsOverview
                    ? PERFORMANCE_BLOCKS.map((item) => <SinglePerformance key={item.title} item={item} />)
                    : performanceList.map((item) => <SinglePerformance key={item.id} item={item} />)}
            </Row>
            {!isBlocked ? (
                <RedirectLink
                    data-e2e='home_performance_link'
                    style={{ ...TOP_INDENTS.XL_PLUS, paddingRight: 16 }}
                    text='See detailed performance'
                    fontSize='16px'
                    to='/performance'
                />
            ) : null}
            {!isBlocked && (
                <Fragment>
                    <Divider style={{ borderBlockStart: `1px solid ${COLORS.$gray20Black}` }} margin='48px 0' />
                    <SectionHeader
                        data-e2e='home_breakdowns_title'
                        title='Performance breakdowns'
                        style={BOTTOM_INDENTS.L}
                    />
                    <div style={{ position: 'relative' }}>
                        {pendingBreakdowns && <Loader overlayColor='rgba(244, 244, 244, 0.8)' />}
                        <HorizontalCards
                            data-e2e='home_engagements_section'
                            title='Engagement funnel'
                            data={breakdowns?.engagementFunnel}
                            pending={false}
                            type='homepage'
                        />
                        {touchPointFunnelVisible ? (
                            <HorizontalCards
                                title='Touchpoint funnel'
                                hintTitle='Touchpoint funnel'
                                hintBody='When Klickly finds a best-fit consumer and shows them your products, this is called a touchpoint. “Days from first touchpoint” is the time since we first start advertising to the consumer. “Average touchpoints” is the  number of times Klickly touches a consumer to drive them to a purchase. “Days from last touchpoint” is the amount of time from last Klickly touch to when the consumer purchases.'
                                data={breakdowns?.touchpointFunnel}
                                pending={false}
                                type='homepage'
                                data-e2e='home_touchpoints_section'
                            />
                        ) : null}
                        <TopProducts
                            data-e2e='home_top-products_section'
                            title='Top products'
                            data={breakdowns?.products}
                            pending={false}
                            type='homepage'
                        />
                    </div>

                    <RedirectLink
                        data-e2e='home_performance-breakdowns_link'
                        style={{ ...TOP_INDENTS.XL_PLUS, paddingRight: 16, marginTop: 0 }}
                        text='See detailed performance breakdowns'
                        fontSize='16px'
                        to='/performance'
                    />
                    <TopSites data={topSites} pending={pendingTopSites} type='homepage' />
                </Fragment>
            )}
        </div>
    );
});

export default PerformanceSection;
